import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  doc,
  getDocs,
  addDoc,
  deleteDoc,
  updateDoc,
  deleteField,
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import {
  ChevronUp,
  ChevronDown,
  Edit2,
  Trash2,
  Plus,
  X,
  Play,
  Save,
} from "lucide-react";
import {
  getEmbedVideoUrl,
  isIsiLivePlayerUrl,
  normalizeVideoUrl,
} from "../utils/rokuUrl";

const EpisodeManager = ({ seriesId, seriesTitle, seriesThumbnail }) => {
  const [episodes, setEpisodes] = useState([]);
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [editingEpisode, setEditingEpisode] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [newEpisode, setNewEpisode] = useState({
    id: uuidv4(),
    title: "",
    videoUrl: "",
    language: "en-US",
    episodeNumber: 1,
    shortDescription: "",
    longDescription: "",
    releaseDate: new Date().toISOString(),
    rating: { rating: "0", ratingSource: "BBFC" },
    thumbnail: seriesThumbnail,
    duration: "00:00:01",
    onRoku: false,
    sp2: false,
    sp2RokuUrl: "",
    sp2LegacyVideoUrl: "",
  });

  useEffect(() => {
    fetchEpisodes();
  }, [seriesId]);

  const isRokuOnlyHlsUrl = (url) => {
    if (typeof url !== "string" || url.length === 0) return false;
    const normalized = normalizeVideoUrl(url);
    return normalized.startsWith("http") && normalized.endsWith(".m3u8");
  };

  const isRokuEligible = (url, sp2Enabled = false, sp2RokuUrl = "") => {
    if (sp2Enabled) {
      return isRokuOnlyHlsUrl(sp2RokuUrl);
    }
    if (typeof url !== "string" || url.length === 0) return false;

    const normalizedUrl = normalizeVideoUrl(url);

    if (
      normalizedUrl.startsWith("https://video.isilive.ca/") &&
      normalizedUrl.endsWith(".mp4")
    ) {
      return true;
    }
    if (normalizedUrl.startsWith("https://video.isilive.ca/play/")) return true;
    return !normalizedUrl.startsWith("http");
  };

  const needsLegacyStatusUrl = (url, sp2Enabled = false) => {
    return sp2Enabled || isIsiLivePlayerUrl(normalizeVideoUrl(url));
  };

  const fetchEpisodes = async () => {
    try {
      const episodesSnapshot = await getDocs(
        collection(db, `content/main/series/${seriesId}/episodes`),
      );
      const episodesData = episodesSnapshot.docs.map((doc) => ({
        episodeId: doc.id,
        ...doc.data(),
      }));
      setEpisodes(
        episodesData.sort((a, b) => a.episodeNumber - b.episodeNumber),
      );
    } catch (error) {
      console.error("Error fetching episodes:", error);
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "videoUrl") {
      const rokuEligible = isRokuEligible(
        value,
        !!newEpisode.sp2,
        newEpisode.sp2RokuUrl,
      );
      setNewEpisode({
        ...newEpisode,
        [name]: value,
        onRoku: rokuEligible ? newEpisode.onRoku : false,
      });
    } else if (name === "sp2RokuUrl") {
      const rokuEligible = isRokuEligible(
        newEpisode.videoUrl,
        !!newEpisode.sp2,
        value,
      );
      setNewEpisode({
        ...newEpisode,
        sp2RokuUrl: value,
        onRoku: rokuEligible ? newEpisode.onRoku : false,
      });
    } else if (name === "sp2LegacyVideoUrl") {
      setNewEpisode({
        ...newEpisode,
        sp2LegacyVideoUrl: value,
      });
    } else if (name === "sp2" && type === "checkbox") {
      const rokuEligible = isRokuEligible(
        newEpisode.videoUrl,
        checked,
        newEpisode.sp2RokuUrl,
      );
      setNewEpisode({
        ...newEpisode,
        sp2: checked,
        onRoku: rokuEligible ? newEpisode.onRoku : false,
      });
    } else if (type === "checkbox") {
      setNewEpisode({ ...newEpisode, [name]: checked });
    } else {
      setNewEpisode({ ...newEpisode, [name]: value });
    }
  };

  const isYouTubeEmbed = (url) => {
    return url.includes("youtube.com/embed/") || url.includes("youtu.be/");
  };

  // Determine video type and convert URL for videos.url
  const processVideoUrl = (videoUrl, sp2Enabled = false, sp2RokuUrl = "") => {
    if (sp2Enabled && isRokuOnlyHlsUrl(sp2RokuUrl)) {
      const normalizedRokuUrl = normalizeVideoUrl(sp2RokuUrl);
      return {
        videosUrl: normalizedRokuUrl,
        videoType: "HLS",
        quality: "HD",
      };
    }

    if (!videoUrl) {
      return {
        videosUrl: "",
        videoType: "External",
        quality: "HD",
      };
    }

    const normalizedInput = normalizeVideoUrl(videoUrl);

    if (isYouTubeEmbed(normalizedInput)) {
      return {
        videosUrl: "",
        videoType: "YouTube",
        quality: "HD",
      };
    }

    if (
      normalizedInput.startsWith("https://video.isilive.ca/") &&
      normalizedInput.endsWith(".mp4")
    ) {
      return {
        videosUrl: normalizedInput,
        videoType: "MP4",
        quality: "HD",
      };
    }

    if (
      sp2Enabled &&
      normalizedInput.startsWith("https://video.isilive.ca/") &&
      !normalizedInput.startsWith("https://video.isilive.ca/play/")
    ) {
      const streamPath = normalizedInput
        .replace("https://video.isilive.ca/", "")
        .replace(/^\/+|\/+$/g, "");
      return {
        videosUrl: `https://linx.isilive.ca/live/${streamPath}/index.m3u8`,
        videoType: "HLS",
        quality: "HD",
      };
    }

    if (normalizedInput.startsWith("https://video.isilive.ca/play/")) {
      if (normalizedInput.endsWith(".mp4")) {
        const filePath = normalizedInput.replace(
          "https://video.isilive.ca/play/",
          "",
        );
        return {
          videosUrl: `https://video.isilive.ca/${filePath}`,
          videoType: "MP4",
          quality: "HD",
        };
      }

      const streamPath = normalizedInput.replace(
        "https://video.isilive.ca/play/",
        "",
      );
      return {
        videosUrl: `https://linx.isilive.ca/live/${streamPath}/index.m3u8`,
        videoType: "HLS",
        quality: "HD",
      };
    }

    if (!normalizedInput.startsWith("http")) {
      if (normalizedInput.endsWith(".mp4")) {
        return {
          videosUrl: `https://video.isilive.ca/${normalizedInput}`,
          videoType: "MP4",
          quality: "HD",
        };
      }

      return {
        videosUrl: `https://linx.isilive.ca/live/${normalizedInput}/index.m3u8`,
        videoType: "HLS",
        quality: "HD",
      };
    }

    if (isIsiLivePlayerUrl(normalizedInput)) {
      return {
        videosUrl: "",
        videoType: "iSiLIVE Player",
        quality: "HD",
      };
    }

    return {
      videosUrl: "",
      videoType: "External",
      quality: "HD",
    };
  };

  const handleAddEpisode = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    const normalizedUrl = normalizeVideoUrl(newEpisode.videoUrl);
    const { videosUrl, videoType, quality } = processVideoUrl(
      normalizedUrl,
      !!newEpisode.sp2,
      newEpisode.sp2RokuUrl,
    );
    const { sp2RokuUrl, ...episodeBase } = newEpisode;

    const episode = {
      ...episodeBase,
      videoUrl: normalizedUrl,
      sp2LegacyVideoUrl: newEpisode.sp2LegacyVideoUrl
        ? normalizeVideoUrl(newEpisode.sp2LegacyVideoUrl)
        : "",
      content: {
        dateAdded: new Date().toISOString(),
        duration: timeToSeconds(newEpisode.duration),
        language: "en-US",
        videos: videosUrl
          ? [
              {
                url: videosUrl,
                quality: quality,
                videoType: videoType,
              },
            ]
          : [],
      },
    };

    try {
      await addDoc(
        collection(db, `content/main/series/${seriesId}/episodes`),
        episode,
      );
      await fetchEpisodes();
      resetForm();
      setIsDropdownOpen(false);
    } catch (error) {
      console.error("Error adding episode:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditEpisode = async (e) => {
    e.preventDefault();
    if (!editingEpisode || isSubmitting) return;

    setIsSubmitting(true);
    const normalizedUrl = normalizeVideoUrl(newEpisode.videoUrl);
    const { videosUrl, videoType, quality } = processVideoUrl(
      normalizedUrl,
      !!newEpisode.sp2,
      newEpisode.sp2RokuUrl,
    );
    const { sp2RokuUrl, ...episodeBase } = newEpisode;

    const updatedEpisode = {
      ...episodeBase,
      videoUrl: normalizedUrl,
      sp2LegacyVideoUrl: newEpisode.sp2LegacyVideoUrl
        ? normalizeVideoUrl(newEpisode.sp2LegacyVideoUrl)
        : "",
      content: {
        ...newEpisode.content,
        duration: timeToSeconds(newEpisode.duration),
        videos: videosUrl
          ? [
              {
                url: videosUrl,
                quality: quality,
                videoType: videoType,
              },
            ]
          : [],
      },
    };

    try {
      await updateDoc(
        doc(
          db,
          `content/main/series/${seriesId}/episodes/${editingEpisode.episodeId}`,
        ),
        {
          ...updatedEpisode,
          sp2RokuUrl: deleteField(),
        },
      );
      await fetchEpisodes();
      resetForm();
      setEditingEpisode(null);
    } catch (error) {
      console.error("Error updating episode:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteEpisode = async (episodeId) => {
    if (window.confirm("Are you sure you want to delete this episode?")) {
      try {
        await deleteDoc(
          doc(db, `content/main/series/${seriesId}/episodes/${episodeId}`),
        );
        await fetchEpisodes();
      } catch (error) {
        console.error("Error deleting episode:", error);
      }
    }
  };

  const handleMoveEpisode = async (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= episodes.length) return;

    const updatedEpisodes = [...episodes];
    const [movedEpisode] = updatedEpisodes.splice(fromIndex, 1);
    updatedEpisodes.splice(toIndex, 0, movedEpisode);

    // Update episode numbers
    const updates = updatedEpisodes.map((episode, index) => ({
      ...episode,
      episodeNumber: index + 1,
    }));

    try {
      await Promise.all(
        updates.map((episode) =>
          updateDoc(
            doc(
              db,
              `content/main/series/${seriesId}/episodes/${episode.episodeId}`,
            ),
            { episodeNumber: episode.episodeNumber },
          ),
        ),
      );
      setEpisodes(updates);
    } catch (error) {
      console.error("Error reordering episodes:", error);
    }
  };

  const timeToSeconds = (duration) => {
    if (!duration) return 0;
    if (typeof duration === "number") return duration;
    const [hours, minutes, seconds] = duration.split(":").map(Number);
    return hours * 3600 + minutes * 60 + seconds;
  };

  const secondsToTimeFormat = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  const resetForm = () => {
    setNewEpisode({
      id: uuidv4(),
      title: "",
      videoUrl: "",
      language: "en-US",
      episodeNumber: episodes.length + 1,
      shortDescription: "",
      longDescription: "",
      releaseDate: new Date().toISOString(),
      rating: { rating: "0", ratingSource: "BBFC" },
      thumbnail: seriesThumbnail,
      duration: "00:00:01",
      onRoku: false,
      sp2: false,
      sp2RokuUrl: "",
      sp2LegacyVideoUrl: "",
    });
    setEditingEpisode(null);
  };

  // Get video type display text
  const getVideoTypeDisplay = (episode) => {
    if (isYouTubeEmbed(episode.videoUrl)) {
      return "YouTube";
    }
    return episode.content?.videos[0]?.videoType || "Unknown";
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="w-full bg-gray-900 text-white rounded-lg overflow-hidden">
        <div className="p-4 md:p-6 border-b border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <h2 className="text-xl md:text-2xl font-bold">
              Episodes - {seriesTitle}
            </h2>
            <button
              onClick={() => {
                if (!isDropdownOpen) {
                  resetForm(); // Call resetForm only when opening the dropdown
                }
                setIsDropdownOpen(!isDropdownOpen);
              }}
              className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              {isDropdownOpen ? <X size={20} /> : <Plus size={20} />}
              {isDropdownOpen ? "Cancel" : "Add New Episode"}
            </button>
          </div>
        </div>
        {/* Add/Edit Episode Form */}
        {isDropdownOpen && (
          <div className="p-4 md:p-6">
            <h3 className="text-xl font-bold mb-4">
              {editingEpisode ? "Edit Episode" : "Add New Episode"}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                name="title"
                value={newEpisode.title}
                onChange={handleInputChange}
                placeholder="Episode Title"
                className="w-full p-3 bg-gray-700 rounded-lg"
              />
              <div className="relative">
                <input
                  type="text"
                  name="videoUrl"
                  value={newEpisode.videoUrl}
                  onChange={handleInputChange}
                  placeholder="Web video URL: iSiLIVE play URL, SP2 direct page URL, legacy short path, player.isilive.ca/play URL, or YouTube embed"
                  className="w-full p-3 bg-gray-700 rounded-lg"
                />
                <div className="text-xs text-gray-400 mt-1">
                  Supported: iSiLIVE full play URLs, legacy short paths, SP2
                  direct video page URLs, embeddable player.isilive.ca/play
                  URLs, and YouTube embeds
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="sp2"
                  id="sp2"
                  checked={!!newEpisode.sp2}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="sp2" className="text-sm text-gray-300">
                  SP2 (enable separate Roku-only m3u8 URL)
                </label>
              </div>
              {!!newEpisode.sp2 && (
                <div className="relative">
                  <input
                    type="text"
                    name="sp2RokuUrl"
                    value={newEpisode.sp2RokuUrl || ""}
                    onChange={handleInputChange}
                    placeholder="SP2 Roku-only m3u8 URL (https://.../index.m3u8)"
                    className="w-full p-3 bg-gray-700 rounded-lg"
                  />
                  <div className="text-xs text-gray-400 mt-1">
                    This URL is used for Roku only and should end with .m3u8.
                  </div>
                </div>
              )}
              {needsLegacyStatusUrl(newEpisode.videoUrl, !!newEpisode.sp2) && (
                <div className="relative">
                  <input
                    type="text"
                    name="sp2LegacyVideoUrl"
                    value={newEpisode.sp2LegacyVideoUrl || ""}
                    onChange={handleInputChange}
                    placeholder="Legacy play URL for live status (https://video.isilive.ca/play/... )"
                    className="w-full p-3 bg-gray-700 rounded-lg"
                  />
                  <div className="text-xs text-gray-400 mt-1">
                    Required for live-status checks on SP2 direct URLs and
                    player.isilive.ca embeds. Example:
                    https://video.isilive.ca/play/isivideo/testloop
                  </div>
                </div>
              )}
              {/* Only show Roku checkbox if URL is eligible */}
              {isRokuEligible(
                newEpisode.videoUrl,
                !!newEpisode.sp2,
                newEpisode.sp2RokuUrl,
              ) && (
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="onRoku"
                    id="onRoku"
                    checked={newEpisode.onRoku}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="onRoku" className="text-sm text-gray-300">
                    Available on Roku
                  </label>
                </div>
              )}
              {!isRokuEligible(
                newEpisode.videoUrl,
                !!newEpisode.sp2,
                newEpisode.sp2RokuUrl,
              ) &&
                newEpisode.videoUrl && (
                  <div className="text-sm text-gray-400">
                    Roku availability: iSiLIVE play URLs, legacy short paths,
                    and direct MP4 URLs. In SP2 mode, enter a valid Roku m3u8
                    URL in the separate SP2 field.
                  </div>
                )}
              <div></div> {/* Empty div for grid spacing */}
              <input
                type="number"
                name="episodeNumber"
                value={newEpisode.episodeNumber}
                onChange={handleInputChange}
                placeholder="Episode Number"
                className="w-full p-3 bg-gray-700 rounded-lg"
              />
              <input
                type="text"
                name="duration"
                value={newEpisode.duration}
                onChange={handleInputChange}
                placeholder="Duration (HH:MM:SS)"
                className="w-full p-3 bg-gray-700 rounded-lg"
              />
              <div className="col-span-1 md:col-span-2">
                <textarea
                  name="shortDescription"
                  value={newEpisode.shortDescription}
                  onChange={handleInputChange}
                  placeholder="Short Description"
                  className="w-full p-3 bg-gray-700 rounded-lg"
                  rows="2"
                />
              </div>
              <div className="col-span-1 md:col-span-2">
                <textarea
                  name="longDescription"
                  value={newEpisode.longDescription}
                  onChange={handleInputChange}
                  placeholder="Long Description"
                  className="w-full p-3 bg-gray-700 rounded-lg"
                  rows="3"
                />
              </div>
              <div className="col-span-1 md:col-span-2 flex gap-2">
                <button
                  onClick={
                    editingEpisode ? handleEditEpisode : handleAddEpisode
                  }
                  disabled={isSubmitting}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-lg transition-colors"
                >
                  {isSubmitting
                    ? "Saving..."
                    : editingEpisode
                      ? "Save Changes"
                      : "Add Episode"}
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="space-y-4 m-2">
          {episodes.map((episode, index) => (
            <div
              key={episode.episodeId}
              className="bg-gray-800 rounded-lg p-4 shadow-lg transition-all hover:shadow-xl"
            >
              <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-lg md:text-xl font-semibold">
                      Episode {episode.episodeNumber}:
                    </span>
                    <h3 className="text-lg md:text-xl">{episode.title}</h3>
                  </div>
                  <p className="text-gray-400 mt-2">
                    Short Description: {episode.shortDescription}
                  </p>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-400 mt-2">
                    <span>
                      Duration: {secondsToTimeFormat(episode.content?.duration)}
                    </span>
                    <span>Video Type: {getVideoTypeDisplay(episode)}</span>
                    <span>On Roku: {episode.onRoku ? "Yes" : "No"}</span>
                    <span>Views: {episode.viewsTotal || 0}</span>
                  </div>
                </div>
                <div className="flex gap-2 w-full md:w-auto justify-end">
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleMoveEpisode(index, index - 1)}
                      className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={index === 0}
                    >
                      <ChevronUp size={20} />
                    </button>
                    <button
                      onClick={() => handleMoveEpisode(index, index + 1)}
                      className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={index === episodes.length - 1}
                    >
                      <ChevronDown size={20} />
                    </button>
                  </div>
                  <button
                    onClick={() =>
                      window.open(getEmbedVideoUrl(episode.videoUrl))
                    }
                    className="p-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                  >
                    <Play size={20} />
                  </button>
                  <button
                    onClick={() => {
                      setEditingEpisode(episode);
                      setNewEpisode({
                        ...episode,
                        sp2: !!episode.sp2,
                        sp2RokuUrl:
                          episode.sp2RokuUrl ||
                          episode.content?.videos?.[0]?.url ||
                          "",
                        sp2LegacyVideoUrl: episode.sp2LegacyVideoUrl || "",
                      });
                      setIsDropdownOpen(true);
                    }}
                    className="p-2 bg-yellow-600 hover:bg-yellow-700 rounded-lg transition-colors"
                  >
                    <Edit2 size={20} />
                  </button>
                  <button
                    onClick={() => handleDeleteEpisode(episode.episodeId)}
                    className="p-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EpisodeManager;
